import React, { useState, useCallback, useEffect, useRef } from "react"
import type {
  ActionEditorProps,
  Condition,
  Step,
  StepConfig,
  IslandFormState,
  SelectOption,
  ProviderHints,
  VariableGroups,
  ActionPreviewResult,
  PreviewEvent,
} from "./types"
import { DEFAULT_STEP_CONFIGS, FIELD_OPTIONS, INPUT_CLASS_SM } from "./constants"
import { XIcon, PlusIcon } from "./icons"
import { ConditionRow } from "./ConditionRow"
import { SortableStepList } from "./SortableStepList"

function humanizeField(field: string): string {
  return field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export function newCondition(): Condition {
  return { field: "provider", operator: "equals", value: "" }
}

export function newStep(position: number, canForward = true): Step {
  return {
    config_version: 1,
    step_type: canForward ? "forward" : "",
    name: "",
    config: canForward
      ? { url: "", timeout_seconds: 30, retry_count: 0 }
      : {},
    enabled: true,
    failure_policy: null,
    position,
  }
}

interface ActionEditorFormProps {
  form: IslandFormState
  initialAction: ActionEditorProps["initialAction"]
  cancelUrl: string
  isEdit: boolean
  canForward: boolean
  canComposeActions?: boolean
  canExecuteHttpRequest?: boolean
  billingUrl: string
  channels: SelectOption[]
  tags: SelectOption[]
  ignoreReasons: SelectOption[]
  serverErrors?: string[]
  conditionHints?: Record<string, ProviderHints>
  endpointProvider?: string | null
  seenEventTypes?: string[]
  variableHints?: VariableGroups
  previewUrl?: string
  previewEvents?: PreviewEvent[]
}

export function ActionEditorForm({
  form,
  initialAction,
  cancelUrl,
  isEdit,
  canForward,
  canComposeActions = false,
  canExecuteHttpRequest = false,
  billingUrl,
  channels,
  tags,
  ignoreReasons,
  serverErrors,
  conditionHints,
  endpointProvider,
  seenEventTypes,
  variableHints,
  previewUrl,
  previewEvents = [],
}: ActionEditorFormProps) {
  const [name, setName] = useState(initialAction.name)
  const [description, setDescription] = useState(initialAction.description)
  const [enabled, setEnabled] = useState(initialAction.enabled)
  const [failurePolicy, setFailurePolicy] = useState(
    initialAction.failure_policy
  )
  const [conditions, setConditions] = useState<Condition[]>(
    initialAction.trigger_conditions || []
  )
  const [steps, setSteps] = useState<Step[]>(
    initialAction.steps.length > 0
      ? initialAction.steps
      : [newStep(1, canForward)]
  )
  const [previewEventId, setPreviewEventId] = useState(previewEvents[0]?.id || "")
  const [previewResult, setPreviewResult] = useState<ActionPreviewResult | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)

  const selectedProvider = conditions.find(
    (c) => c.field === "provider" && c.operator === "equals" && c.value
  )?.value

  const effectiveProvider = selectedProvider || endpointProvider || null

  const getValueSuggestions = useCallback(
    (field: string): string[] => {
      if (!conditionHints) return []
      if (field === "provider") return Object.keys(conditionHints).sort()

      if (field === "provider_event_type") {
        const seen = seenEventTypes || []
        let registry: string[] = []
        if (effectiveProvider && conditionHints[effectiveProvider]) {
          registry = conditionHints[effectiveProvider].event_types || []
        }
        const merged = [...seen, ...registry]
        return merged.filter((v, i, a) => a.indexOf(v) === i).sort()
      }
      if (field === "provider_event_category" && effectiveProvider) {
        return conditionHints[effectiveProvider]?.categories || []
      }
      return []
    },
    [conditionHints, effectiveProvider, seenEventTypes]
  )

  const conditionFieldOptions = React.useMemo(() => {
    const providerFields =
      effectiveProvider && conditionHints?.[effectiveProvider]
        ? conditionHints[effectiveProvider].fields || []
        : []
    const options = [...FIELD_OPTIONS, ...providerFields]
    conditions.forEach((condition) => {
      if (!options.some((option) => option.value === condition.field)) {
        options.push({ label: humanizeField(condition.field), value: condition.field })
      }
    })
    return options
  }, [conditionHints, conditions, effectiveProvider])

  const allErrors = [
    ...(serverErrors || []),
    ...(form.transportError
      ? [`Something went wrong: ${form.transportError.message}`]
      : []),
    ...Object.entries(form.errors).flatMap(([field, messages]) =>
      messages.map((msg) => {
        if (field === "base" || field === "_base") return msg
        return `${humanizeField(field)} ${msg}`
      })
    ),
  ]

  const clearFieldError = useCallback(
    (field: string) => {
      if (form.errors[field]) {
        form.clearErrors(field)
      }
    },
    [form]
  )

  const addCondition = useCallback(() => {
    setConditions((prev) => [...prev, newCondition()])
  }, [])

  const removeCondition = useCallback((index: number) => {
    setConditions((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const updateCondition = useCallback(
    (index: number, field: keyof Condition, value: string) => {
      setConditions((prev) =>
        prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
      )
    },
    []
  )

  const addStep = useCallback(() => {
    setSteps((prev) => [...prev, newStep(prev.length + 1, canForward)])
  }, [canForward])

  const removeStep = useCallback((index: number) => {
    setSteps((prev) => {
      const step = prev[index]
      if (step.id) {
        return prev.map((s, i) =>
          i === index ? { ...s, _destroy: true } : s
        )
      }
      return prev.filter((_, i) => i !== index)
    })
  }, [])

  const updateStep = useCallback(
    (index: number, updates: Partial<Step>) => {
      setSteps((prev) =>
        prev.map((s, i) => (i === index ? { ...s, ...updates } : s))
      )
    },
    []
  )

  const updateStepConfig = useCallback(
    (index: number, configUpdates: Partial<StepConfig>) => {
      setSteps((prev) =>
        prev.map((s, i) =>
          i === index
            ? { ...s, config: { ...s.config, ...configUpdates } }
            : s
        )
      )
    },
    []
  )

  const changeStepType = useCallback(
    (index: number, newType: string) => {
      setSteps((prev) =>
        prev.map((s, i) =>
          i === index
            ? {
                ...s,
                step_type: newType,
                config: DEFAULT_STEP_CONFIGS[newType] || {},
              }
            : s
        )
      )
    },
    []
  )

  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        window.location.href = cancelUrl
        return
      }
      if (e.key !== "Tab" || !dialogRef.current) return
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [cancelUrl])

  const titleId = "action-editor-title"

  const runPreview = useCallback(async () => {
    if (!previewUrl || !previewEventId) return

    setPreviewing(true)
    setPreviewError(null)
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
      const response = await fetch(previewUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { "X-CSRF-Token": token } : {}),
        },
        body: JSON.stringify({
          preview: { ingress_event_id: previewEventId },
          endpoint_action: {
            name,
            description,
            enabled,
            failure_policy: failurePolicy,
            trigger_conditions: conditions,
            steps: steps.map((step, index) => ({ ...step, position: index + 1 })),
          },
        }),
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.message || payload.error || "Preview could not be completed")
      setPreviewResult(payload.data as ActionPreviewResult)
    } catch (error) {
      setPreviewResult(null)
      setPreviewError(error instanceof Error ? error.message : "Preview could not be completed")
    } finally {
      setPreviewing(false)
    }
  }, [conditions, description, enabled, failurePolicy, name, previewEventId, previewUrl, steps])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        ref={dialogRef}
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 overflow-y-auto"
        style={{ maxHeight: "90vh" }}
      >
        <div className="divide-y divide-gray-100">
          {/* Hidden fields for Archipelago form submission */}
          <input type="hidden" name="name" value={name} />
          <input type="hidden" name="description" value={description} />
          <input
            type="hidden"
            name="enabled"
            value={enabled ? "true" : "false"}
          />
          <input type="hidden" name="failure_policy" value={failurePolicy} />
          <input
            type="hidden"
            name="trigger_conditions"
            value={JSON.stringify(conditions)}
          />
          <input
            type="hidden"
            name="steps"
            value={JSON.stringify(
              steps.map((s, i) => ({
                ...s,
                position: i + 1,
                config: s.config,
              }))
            )}
          />

          {/* Header */}
          <div className="px-6 py-4 flex items-center justify-between">
            <h2 id={titleId} className="text-lg font-semibold text-gray-900">
              {isEdit ? "Edit Action" : "New Action"}
            </h2>
            <a
              href={cancelUrl}
              className="text-gray-400 hover:text-gray-600"
              data-turbo-frame="action_modal"
              aria-label="Close"
            >
              <XIcon />
            </a>
          </div>

          {/* Errors */}
          {allErrors.length > 0 && (
            <div className="px-6 py-3 bg-red-50 border-b border-red-100">
              <div className="text-sm text-red-700">
                {allErrors.map((msg, i) => (
                  <p key={i}>{msg}</p>
                ))}
              </div>
            </div>
          )}

          {/* Basic info */}
          <div className="px-6 py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label
                  htmlFor="action-name"
                  className="block text-xs font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  id="action-name"
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    clearFieldError("name")
                  }}
                  className={`${INPUT_CLASS_SM} ${
                    form.errors.name ? "border-red-300" : ""
                  }`}
                  placeholder="e.g. Forward to staging"
                  autoFocus
                />
                {form.errors.name && (
                  <p className="text-xs text-red-600" role="alert">
                    {form.errors.name.join(", ")}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <label
                  htmlFor="action-failure-policy"
                  className="block text-xs font-medium text-gray-700"
                >
                  On step failure
                </label>
                <select
                  id="action-failure-policy"
                  value={failurePolicy}
                  onChange={(e) => setFailurePolicy(e.target.value)}
                  className={INPUT_CLASS_SM}
                >
                  <option value="continue">Continue to next step</option>
                  <option value="stop">Stop execution</option>
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label
                htmlFor="action-description"
                className="block text-xs font-medium text-gray-700"
              >
                Description
              </label>
              <textarea
                id="action-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className={INPUT_CLASS_SM}
                placeholder="Optional description..."
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                id="action-enabled"
              />
              <label htmlFor="action-enabled" className="text-sm text-gray-700">
                Enabled
              </label>
            </div>
          </div>

          {/* Trigger Conditions */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  Trigger Conditions
                </h3>
                <p className="text-xs text-gray-500">
                  When should this action run? Leave empty to run on every
                  request.
                </p>
              </div>
              <button
                type="button"
                onClick={addCondition}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100"
              >
                <PlusIcon />
                <span className="ml-0.5">Add Condition</span>
              </button>
            </div>

            <div>
              {conditions.length === 0 ? (
                <p className="text-xs text-gray-400 py-2 text-center">
                  All requests will trigger this action
                </p>
              ) : (
                conditions.map((cond, idx) => (
                  <ConditionRow
                    key={idx}
                    condition={cond}
                    index={idx}
                    onChange={updateCondition}
                    onRemove={removeCondition}
                    valueSuggestions={getValueSuggestions(cond.field)}
                    fieldOptions={conditionFieldOptions}
                  />
                ))
              )}
            </div>
          </div>

          {/* Steps */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-medium text-gray-900">Steps</h3>
                <p className="text-xs text-gray-500">
                  Steps execute in order when conditions match.
                </p>
                {!canForward && (
                  <p className="mt-1 text-xs text-amber-700" role="note">
                    Forward steps require an active paid subscription.{" "}
                    <a
                      href={billingUrl}
                      className="font-medium underline hover:text-amber-900"
                    >
                      Manage Billing
                    </a>
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={addStep}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100"
              >
                <PlusIcon />
                <span className="ml-0.5">Add Step</span>
              </button>
            </div>

            <SortableStepList
              steps={steps}
              onReorder={setSteps}
              onUpdate={updateStep}
              onUpdateConfig={updateStepConfig}
              onChangeType={changeStepType}
              onRemove={removeStep}
              channels={channels}
              tags={tags}
              ignoreReasons={ignoreReasons}
              canForward={canForward}
              canComposeActions={canComposeActions}
              canExecuteHttpRequest={canExecuteHttpRequest}
              billingUrl={billingUrl}
              variableHints={variableHints}
            />
          </div>

          {/* Network-free preview */}
          {previewUrl && (
            <div className="px-6 py-4 bg-indigo-50/40" aria-label="Action preview">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Validate preview</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Preview validates templates and transforms without sending an HTTP request. Secret references appear as placeholders.
                  </p>
                </div>
                {previewEvents.length > 0 && (
                  <div className="flex items-center gap-2">
                    <label htmlFor="preview-event" className="sr-only">Captured event</label>
                    <select
                      id="preview-event"
                      aria-label="Captured event"
                      value={previewEventId}
                      onChange={(event) => setPreviewEventId(event.target.value)}
                      className={`${INPUT_CLASS_SM} max-w-xs`}
                    >
                      {previewEvents.map((event) => <option key={event.id} value={event.id}>{event.label}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={runPreview}
                      disabled={previewing || !previewEventId}
                      className="px-3 py-2 text-xs font-medium text-indigo-700 bg-white border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:opacity-50"
                    >
                      {previewing ? "Validating..." : "Validate"}
                    </button>
                  </div>
                )}
              </div>
              {previewEvents.length === 0 && <p className="mt-2 text-xs text-gray-500">Capture a webhook or email on this endpoint before previewing.</p>}
              {previewError && <p className="mt-2 text-xs text-red-700" role="alert">{previewError}</p>}
              {previewResult && (
                <div className="mt-3 space-y-2" role="status">
                  <p className="text-xs font-medium text-gray-700">Preview {previewResult.status}</p>
                  {previewResult.steps.map((step, index) => (
                    <div key={`${step.reference_key || step.step_type || "step"}-${index}`} className="rounded border border-gray-200 bg-white p-2 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-gray-700">{step.name || step.step_type || `Step ${index + 1}`}</span>
                        <span className={step.status === "failed" ? "text-red-600" : step.status === "previewed" ? "text-green-600" : "text-gray-500"}>{step.status}</span>
                      </div>
                      {step.error_code && <p className="mt-1 text-red-600">{step.error_code}: {step.message}</p>}
                      {step.sensitive_fields && step.sensitive_fields.length > 0 && <p className="mt-1 text-amber-700">Sensitive fields redacted: {step.sensitive_fields.join(", ")}</p>}
                      {step.output && <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] text-gray-500">{JSON.stringify(step.output, null, 2).slice(0, 2000)}</pre>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 flex items-center justify-end gap-3 bg-gray-50 rounded-b-xl">
            <a
              href={cancelUrl}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={form.processing}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 cursor-pointer disabled:opacity-50"
            >
              {form.processing
                ? "Saving..."
                : isEdit
                  ? "Update Action"
                  : "Create Action"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
